import React, { useEffect, useState, useRef } from 'react';
import { X, FileSpreadsheet, FileText, Download, CheckCircle, AlertCircle, Minimize2 } from 'lucide-react';

const ExportCard = ({ 
  isOpen, 
  onClose, 
  onExport, 
  isLoading = false,
  exportProgress = 0,
  status = 'idle', // 'idle', 'loading', 'success', 'error'
  message = '',
  type = 'excel',
  autoClose = true,
  autoCloseDelay = 5000
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const autoCloseTimerRef = useRef(null);

  // Derive the actual status from isLoading if status is not explicitly set
  const currentStatus = isLoading ? 'loading' : status;

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setHasDownloaded(false);
    } else {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsMinimized(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Auto-close logic - only after download and when not hovered
  useEffect(() => {
    if (hasDownloaded && autoClose && !isHovered) {
      autoCloseTimerRef.current = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
    }

    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, [hasDownloaded, autoClose, autoCloseDelay, onClose, isHovered]);

  // Clear timer when hovering
  useEffect(() => {
    if (isHovered && autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
  }, [isHovered]);

  if (!isVisible) return null;

  const handleExport = () => {
    onExport();
    if (currentStatus === 'success') {
      setHasDownloaded(true);
    }
  };

  const getIcon = () => {
    if (isMinimized) return null;
    
    switch (currentStatus) {
      case 'loading':
        return (
          <div className="relative w-8 h-8">
            <svg className="animate-spin h-8 w-8">
              <circle
                className="opacity-25"
                cx="16"
                cy="16"
                r="14"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
              />
              <circle
                className="opacity-75"
                cx="16"
                cy="16"
                r="14"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
                strokeDasharray={88}
                strokeDashoffset={88 - (88 * exportProgress) / 100}
                transform="rotate(-90 16 16)"
              />
            </svg>
            {exportProgress > 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold">{exportProgress}%</span>
              </div>
            )}
          </div>
        );
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-8 h-8 text-red-500" />;
      default:
        return type === 'excel' ? (
          <FileSpreadsheet className="w-8 h-8 text-green-600" />
        ) : (
          <FileText className="w-8 h-8 text-red-600" />
        );
    }
  };

  const getCompactMessage = () => {
    switch (currentStatus) {
      case 'loading':
        return `Exporting ${type?.toUpperCase()}... ${exportProgress}%`;
      case 'success':
        return hasDownloaded ? 'Downloaded successfully!' : 'Ready to download';
      case 'error':
        return 'Export failed';
      default:
        return `Export ${type?.toUpperCase()}`;
    }
  };

  return (
    <div 
      className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card Container */}
      <div className={`bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 ${
        isMinimized ? 'w-68' : 'min-w-[320px] max-w-md'
      }`}>
        {/* Minimized View */}
        {isMinimized ? (
          <div className="px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              {type === 'excel' ? (
                <FileSpreadsheet className="w-4 h-4 text-green-600 flex-shrink-0" />
              ) : (
                <FileText className="w-4 h-4 text-red-600 flex-shrink-0" />
              )}
              <span className="text-sm font-medium text-gray-800 truncate">
                {getCompactMessage()}
              </span>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => setIsMinimized(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <Minimize2 className="w-4 h-4 rotate-180" />
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-4 py-2.5 border-b border-amber-200">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-800 text-sm">
                  Export Status
                </h3>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="text-gray-500 hover:text-gray-700 p-1"
                    title="Minimize"
                  >
                    <Minimize2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700 p-1"
                    title="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {type === 'excel' ? 'Excel Export' : 'PDF Export'}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {message || getCompactMessage()}
                  </p>
                  {currentStatus === 'success' && hasDownloaded && autoClose && (
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <span className="inline-block w-1 h-1 bg-green-400 rounded-full animate-pulse"></span>
                      {isHovered ? 'Move cursor away to auto-close' : 'Auto-closing...'}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-3">
                {currentStatus === 'success' && (
                  <button
                    onClick={handleExport}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    {hasDownloaded ? 'Download Again' : 'Download'}
                  </button>
                )}

                {currentStatus === 'error' && (
                  <button
                    onClick={onExport}
                    className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
                  >
                    Retry Export
                  </button>
                )}
              </div>

              {/* Progress Bar */}
              {currentStatus === 'loading' && (
                <div className="mt-3">
                  <div className="bg-gray-200 rounded-full h-1 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-full transition-all duration-300 ease-out"
                      style={{ width: `${exportProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExportCard;

